import { useMemo, useState } from 'react';
import { Activity, Check, ChevronLeft, ChevronRight, Info, Wind } from 'lucide-react';

export type AQLQDomainKey =
  | 'symptoms'
  | 'emotionalFunction'
  | 'activityLimitation'
  | 'environmentalStimuli';

export type AQLQScaleKey = 'limitation' | 'frequency' | 'distress' | 'q31';
export type AQLQSectionKey = 'activities' | 'symptoms1' | 'symptoms2' | 'global';

export interface AQLQQuestionDefinition {
  itemNumber: number;
  prompt: string;
  scale: AQLQScaleKey;
  domain: AQLQDomainKey;
  section: AQLQSectionKey;
  helper?: string;
}

export interface AQLQDomainScore {
  score: number | null;
  answered: number;
  itemNumbers: number[];
}

export interface AQLQResultPayload {
  answersByItem: Record<string, number>;
  total: number;
  answeredCount: number;
  domainScores: Record<AQLQDomainKey, AQLQDomainScore>;
  sourceMissingItems: number[];
  interpretation: string;
  finishedAt?: string;
}

interface AQLQTestProps {
  initial?: Partial<AQLQResultPayload>;
  onCancel?: () => void;
  onNext: (result: AQLQResultPayload) => void;
}

export const AQLQ_SOURCE_MISSING_ITEMS = [] as const;

export const AQLQ_DOMAIN_LABELS: Record<AQLQDomainKey, string> = {
  symptoms: 'Sintomas',
  emotionalFunction: 'Função emocional',
  activityLimitation: 'Limitação de atividades',
  environmentalStimuli: 'Estímulo ambiental',
};

export const AQLQ_SECTION_LABELS: Record<AQLQSectionKey, { title: string; description: string }> = {
  activities: {
    title: 'Atividades e sono',
    description: 'Primeira página do anexo: limitação em atividades do dia a dia e ao dormir.',
  },
  symptoms1: {
    title: 'Sintomas e emoções I',
    description: 'Bloco intermediário do anexo com sintomas, fumaça, tosse e preocupações.',
  },
  symptoms2: {
    title: 'Sintomas e ambiente II',
    description: 'Bloco seguinte com poeira, clima, cheiros, sono e receio de falta de ar.',
  },
  global: {
    title: 'Limitação global',
    description: 'Fechamento do anexo com limitação global nas últimas duas semanas.',
  },
};

export const AQLQ_LIMITATION_OPTIONS = [
  { value: 1, label: '1 — Totalmente limitado' },
  { value: 2, label: '2 — Extremamente limitado' },
  { value: 3, label: '3 — Muito limitado' },
  { value: 4, label: '4 — Moderadamente limitado' },
  { value: 5, label: '5 — Pouco limitado' },
  { value: 6, label: '6 — Muito pouco limitado' },
  { value: 7, label: '7 — Nada limitado' },
] as const;

export const AQLQ_FREQUENCY_OPTIONS = [
  { value: 1, label: '1 — Sempre' },
  { value: 2, label: '2 — Quase sempre' },
  { value: 3, label: '3 — Bastante tempo' },
  { value: 4, label: '4 — Algum tempo' },
  { value: 5, label: '5 — Pouco tempo' },
  { value: 6, label: '6 — Quase nunca' },
  { value: 7, label: '7 — Nunca' },
] as const;

export const AQLQ_DISTRESS_OPTIONS = [
  { value: 1, label: '1 — Muitíssimo' },
  { value: 2, label: '2 — Muito' },
  { value: 3, label: '3 — Bastante' },
  { value: 4, label: '4 — Moderado' },
  { value: 5, label: '5 — Algum' },
  { value: 6, label: '6 — Muito pouco' },
  { value: 7, label: '7 — Nenhum' },
] as const;

export const AQLQ_QUESTIONS: AQLQQuestionDefinition[] = [
  {
    itemNumber: 1,
    prompt: 'Atividades vigorosas (como se apressar, subir escadas, praticar esportes).',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'activities',
  },
  {
    itemNumber: 2,
    prompt: 'Atividades moderadas (como caminhar, fazer o trabalho de casa, cuidar do jardim/quintal, fazer compras, subir escadas).',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'activities',
  },
  {
    itemNumber: 3,
    prompt: 'Atividades sociais (como falar, brincar com crianças/animais de estimação, visitar amigos/parentes).',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'activities',
  },
  {
    itemNumber: 4,
    prompt: 'Atividades relacionadas ao trabalho (tarefas que você tem que fazer no trabalho).',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'activities',
  },
  {
    itemNumber: 5,
    prompt: 'Quando dorme.',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'activities',
  },
  {
    itemNumber: 6,
    prompt: 'Quanto mal estar ou aflição você tem sentido nas 2 últimas semanas como resultado de um aperto no peito?',
    scale: 'distress',
    domain: 'symptoms',
    section: 'activities',
  },
  {
    itemNumber: 7,
    prompt: 'Se sentiu preocupado por ter asma?',
    scale: 'frequency',
    domain: 'emotionalFunction',
    section: 'symptoms1',
  },
  {
    itemNumber: 8,
    prompt: 'Se sentiu com falta de ar por causa de sua asma?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms1',
  },
  {
    itemNumber: 9,
    prompt: 'Teve sintomas da asma devido ao fato de ter sido exposto à fumaça de cigarro?',
    scale: 'frequency',
    domain: 'environmentalStimuli',
    section: 'symptoms1',
  },
  {
    itemNumber: 10,
    prompt: 'Teve chiado no peito?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms1',
  },
  {
    itemNumber: 11,
    prompt: 'Sentiu que teve que evitar uma situação ou um ambiente devido à fumaça de cigarro?',
    scale: 'frequency',
    domain: 'activityLimitation',
    section: 'symptoms1',
  },
  {
    itemNumber: 12,
    prompt: 'Quanto mal estar ou aflição você tem sentido nas últimas duas semanas devido à tosse?',
    scale: 'distress',
    domain: 'symptoms',
    section: 'symptoms1',
  },
  {
    itemNumber: 13,
    prompt: 'Se sentiu frustrado (por que você não pode fazer o que você gosta) por causa de sua asma?',
    scale: 'frequency',
    domain: 'emotionalFunction',
    section: 'symptoms1',
  },
  {
    itemNumber: 14,
    prompt: 'Teve uma sensação de peso no peito?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms1',
  },
  {
    itemNumber: 15,
    prompt: 'Se sentiu preocupado quanto à necessidade de usar medicação para sua asma?',
    scale: 'frequency',
    domain: 'emotionalFunction',
    section: 'symptoms1',
  },
  {
    itemNumber: 16,
    prompt: 'Sentiu a necessidade de pigarrear?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms1',
  },
  {
    itemNumber: 17,
    prompt: 'Sentiu sintomas da asma como resultado de estar exposto à poeira?',
    scale: 'frequency',
    domain: 'environmentalStimuli',
    section: 'symptoms2',
  },
  {
    itemNumber: 18,
    prompt: 'Teve dificuldade para soltar o ar por causa de sua asma?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 19,
    prompt: 'Teve que evitar uma situação ou um ambiente devido à poeira?',
    scale: 'frequency',
    domain: 'activityLimitation',
    section: 'symptoms2',
  },
  {
    itemNumber: 20,
    prompt: 'Acordou de manhã com sintomas da asma?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 21,
    prompt: 'Sentiu medo de não ter disponível sua medicação para asma?',
    scale: 'frequency',
    domain: 'emotionalFunction',
    section: 'symptoms2',
  },
  {
    itemNumber: 22,
    prompt: 'Se sentiu incomodado pela respiração difícil?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 23,
    prompt: 'Teve os sintomas da asma devido ao tempo ruim ou à poluição do ar?',
    scale: 'frequency',
    domain: 'environmentalStimuli',
    section: 'symptoms2',
  },
  {
    itemNumber: 24,
    prompt: 'Se acordou à noite por causa de sua asma?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 25,
    prompt: 'Evitou sair devido ao tempo ruim ou à poluição do ar?',
    scale: 'frequency',
    domain: 'activityLimitation',
    section: 'symptoms2',
  },
  {
    itemNumber: 26,
    prompt: 'Teve sintomas de asma devido à exposição a cheiros fortes ou perfume?',
    scale: 'frequency',
    domain: 'environmentalStimuli',
    section: 'symptoms2',
  },
  {
    itemNumber: 27,
    prompt: 'Teve receio de perder o fôlego?',
    scale: 'frequency',
    domain: 'emotionalFunction',
    section: 'symptoms2',
  },
  {
    itemNumber: 28,
    prompt: 'Sentiu que teve que evitar uma situação ou um ambiente devido a cheiros fortes ou perfumes?',
    scale: 'frequency',
    domain: 'activityLimitation',
    section: 'symptoms2',
  },
  {
    itemNumber: 29,
    prompt: 'Sua asma interferiu em que você tivesse uma boa noite de sono?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 30,
    prompt: 'Teve a sensação de ter que brigar pelo ar?',
    scale: 'frequency',
    domain: 'symptoms',
    section: 'symptoms2',
  },
  {
    itemNumber: 31,
    prompt: 'Pense em todo tipo de atividade que você gostaria de ter feito durante as duas últimas semanas. O quanto suas atividades têm sido limitadas por causa de sua asma?',
    scale: 'q31',
    domain: 'activityLimitation',
    section: 'global',
    helper: 'No anexo, esta escala mostra apenas os âncoras extremos. Aqui, 1 significa maior limitação e 7 significa sem limitação.',
  },
  {
    itemNumber: 32,
    prompt: 'De modo geral, entre todas as atividades que você tem feito durante as duas últimas semanas, o quão limitado você tem estado por causa de sua asma?',
    scale: 'limitation',
    domain: 'activityLimitation',
    section: 'global',
  },
] as const;

const AQLQ_SECTION_ORDER: AQLQSectionKey[] = ['activities', 'symptoms1', 'symptoms2', 'global'];

const AQLQ_DOMAIN_ITEM_NUMBERS: Record<AQLQDomainKey, number[]> = {
  symptoms: [6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 29, 30],
  emotionalFunction: [7, 13, 15, 21, 27],
  activityLimitation: [1, 2, 3, 4, 5, 11, 19, 25, 28, 31, 32],
  environmentalStimuli: [9, 17, 23, 26],
};

const getQuestionOptions = (question: AQLQQuestionDefinition) => {
  switch (question.scale) {
    case 'limitation':
      return AQLQ_LIMITATION_OPTIONS;
    case 'distress':
      return AQLQ_DISTRESS_OPTIONS;
    case 'q31':
      return Array.from({ length: 7 }, (_, index) => ({
        value: index + 1,
        label: `${index + 1} — Grau ${index + 1}/7 de limitação`,
      }));
    case 'frequency':
    default:
      return AQLQ_FREQUENCY_OPTIONS;
  }
};

export const getAQLQAnswerLabel = (question: AQLQQuestionDefinition, value?: number | null): string => {
  if (typeof value !== 'number') return 'Não respondido';
  const option = getQuestionOptions(question).find((item) => item.value === value);
  return option?.label ?? `${value}/7`;
};

const calculateAverage = (values: number[]): number | null => {
  if (!values.length) return null;
  return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
};

const findLowestDomain = (domainScores: Record<AQLQDomainKey, AQLQDomainScore>): { key: AQLQDomainKey; score: number } | null => {
  const candidates = (Object.keys(domainScores) as AQLQDomainKey[])
    .map((key) => ({ key, score: domainScores[key].score }))
    .filter((item): item is { key: AQLQDomainKey; score: number } => typeof item.score === 'number')
    .sort((a, b) => a.score - b.score);

  return candidates[0] ?? null;
};

export const getAQLQInterpretationText = (
  total: number | null,
  domainScores: Record<AQLQDomainKey, AQLQDomainScore>,
  sourceMissingItems: readonly number[] = [],
): string => {
  if (typeof total !== 'number') {
    return 'Não há dados suficientes para interpretar o AQLQ.';
  }

  const lowestDomain = findLowestDomain(domainScores);
  const dominantText = lowestDomain
    ? `O domínio mais comprometido foi ${AQLQ_DOMAIN_LABELS[lowestDomain.key].toLowerCase()} (${lowestDomain.score.toFixed(2)}).`
    : '';
  const sourceNote = sourceMissingItems.length
    ? `O cálculo segue o anexo fornecido, que não apresenta a questão ${sourceMissingItems.join(', ')}.`
    : '';

  return `A média global foi ${total.toFixed(2)}/7; valores maiores representam melhor qualidade de vida relacionada à asma. ${dominantText} Não foi aplicada faixa basal automática de gravidade. ${sourceNote}`.trim();
};

const buildDomainScores = (answersByItem: Record<string, number>): Record<AQLQDomainKey, AQLQDomainScore> =>
  (Object.keys(AQLQ_DOMAIN_LABELS) as AQLQDomainKey[]).reduce<Record<AQLQDomainKey, AQLQDomainScore>>((accumulator, key) => {
    const visibleItems = AQLQ_DOMAIN_ITEM_NUMBERS[key];
    const values = visibleItems
      .map((itemNumber) => answersByItem[String(itemNumber)])
      .filter((value): value is number => typeof value === 'number');

    accumulator[key] = {
      score: calculateAverage(values),
      answered: values.length,
      itemNumbers: visibleItems,
    };
    return accumulator;
  }, {} as Record<AQLQDomainKey, AQLQDomainScore>);

export default function AQLQTest({ initial, onCancel, onNext }: AQLQTestProps) {
  const [answersByItem, setAnswersByItem] = useState<Record<string, number>>(initial?.answersByItem ?? {});
  const [activeSection, setActiveSection] = useState(0);

  const sectionQuestions = useMemo(
    () =>
      AQLQ_SECTION_ORDER.map((sectionKey) => ({
        key: sectionKey,
        ...AQLQ_SECTION_LABELS[sectionKey],
        questions: AQLQ_QUESTIONS.filter((question) => question.section === sectionKey),
      })),
    [],
  );

  const progressBySection = sectionQuestions.map((section) => {
    const answered = section.questions.filter((question) => typeof answersByItem[String(question.itemNumber)] === 'number').length;
    return {
      answered,
      total: section.questions.length,
      complete: answered === section.questions.length,
    };
  });

  const currentSection = sectionQuestions[activeSection];
  const currentProgress = progressBySection[activeSection];
  const allAnswered = AQLQ_QUESTIONS.every((question) => typeof answersByItem[String(question.itemNumber)] === 'number');
  const answeredCount = Object.keys(answersByItem).length;
  const total = calculateAverage(
    AQLQ_QUESTIONS.map((question) => answersByItem[String(question.itemNumber)]).filter((value): value is number => typeof value === 'number'),
  );
  const domainScores = buildDomainScores(answersByItem);
  const interpretation = getAQLQInterpretationText(total, domainScores, AQLQ_SOURCE_MISSING_ITEMS);
  const worstDomain = findLowestDomain(domainScores);

  const setAnswer = (itemNumber: number, value: number) => {
    setAnswersByItem((previous) => ({
      ...previous,
      [String(itemNumber)]: value,
    }));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-900">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5" />
          <div className="space-y-2 text-sm leading-relaxed">
            <p className="font-semibold">AQLQ — Asthma Quality of Life Questionnaire</p>
            <p>
              Considere apenas as <strong>últimas duas semanas</strong>. O formulário está sendo replicado exatamente como no
              anexo enviado.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 md:col-span-2">
          <div className="flex items-center gap-2 text-blue-800">
            <Wind className="h-4 w-4" />
            <span className="text-xs font-black uppercase tracking-[0.25em]">Resumo global</span>
          </div>
          <div className="mt-2 text-lg font-semibold text-slate-900">{total != null ? total.toFixed(2) : '—'}/7</div>
          <p className="mt-2 text-sm text-slate-600">{interpretation}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Questões respondidas</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{answeredCount}/{AQLQ_QUESTIONS.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
            <Activity className="h-4 w-4" />
            Domínio mais comprometido
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {worstDomain ? AQLQ_DOMAIN_LABELS[worstDomain.key] : '—'}
          </div>
          <div className="mt-1 text-xs text-slate-500">{worstDomain ? `${worstDomain.score.toFixed(2)}/7` : 'Preencha o questionário.'}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 pt-4">
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-4">
            {sectionQuestions.map((section, index) => {
              const progress = progressBySection[index];
              const isActive = activeSection === index;
              const isAccessible = index <= activeSection || progressBySection[index - 1]?.complete;

              return (
                <button
                  key={section.key}
                  type="button"
                  disabled={!isAccessible}
                  onClick={() => isAccessible && setActiveSection(index)}
                  className={`rounded-xl border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : isAccessible
                        ? 'border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300 hover:bg-slate-100'
                        : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold">{section.title}</div>
                    <div className={`rounded-full px-2 py-0.5 text-xs font-bold ${isActive ? 'bg-white/15 text-white' : 'bg-white text-slate-600'}`}>
                      {progress.answered}/{progress.total}
                    </div>
                  </div>
                  <div className={`mt-1 text-xs ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>{section.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-lg font-semibold text-slate-900">{currentSection.title}</div>
              <div className="text-sm text-slate-500">{currentSection.description}</div>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
              Etapa {activeSection + 1} de {sectionQuestions.length}
            </div>
          </div>
        </div>

        <div className="px-4 py-4">
          <div className="space-y-4">
            {currentSection.questions.map((question) => {
              const selected = answersByItem[String(question.itemNumber)];
              const options = getQuestionOptions(question);
              return (
                <div key={`aqlq-question-${question.itemNumber}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Questão {question.itemNumber} • {AQLQ_DOMAIN_LABELS[question.domain]}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">{question.prompt}</p>
                      {question.helper && <p className="mt-2 text-xs text-slate-500">{question.helper}</p>}
                    </div>
                    <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                      {typeof selected === 'number' ? `${selected}/7` : '—'}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {options.map((option) => (
                      <label
                        key={`${question.itemNumber}-${option.value}`}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition ${
                          selected === option.value
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          className="hidden"
                          name={`aqlq_v2_${question.itemNumber}`}
                          checked={selected === option.value}
                          onChange={() => setAnswer(question.itemNumber, option.value)}
                        />
                        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
                          {option.value}
                        </span>
                        <span className="flex-1">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
              Cancelar
            </button>
            <div className="text-sm text-slate-600">
              Média: <span className="font-semibold text-slate-900">{total != null ? total.toFixed(2) : '—'}</span>/7
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-200 disabled:opacity-50"
              onClick={() => setActiveSection((current) => Math.max(0, current - 1))}
              disabled={activeSection === 0}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Anterior
            </button>

            {activeSection < sectionQuestions.length - 1 ? (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => setActiveSection((current) => Math.min(sectionQuestions.length - 1, current + 1))}
                disabled={!currentProgress.complete}
              >
                Próxima etapa
                <ChevronRight className="ml-1 h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                disabled={!allAnswered}
                onClick={() =>
                  allAnswered &&
                  onNext({
                    answersByItem,
                    total: total ?? 0,
                    answeredCount,
                    domainScores,
                    sourceMissingItems: [...AQLQ_SOURCE_MISSING_ITEMS],
                    interpretation,
                    finishedAt: new Date().toISOString(),
                  })
                }
              >
                Concluir AQLQ
                <Check className="ml-1 h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
